<script setup>
defineOptions({ name: "SegmentAcouguesPage" });
import { onMounted, onUnmounted, ref, defineAsyncComponent } from "vue";
import {
  ScaleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  TagIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  DocumentCheckIcon,
} from "@heroicons/vue/24/outline";
import {
  CubeIcon,
  Square3Stack3DIcon,
  TagIcon as TagIconSolid,
  ArchiveBoxIcon,
  SparklesIcon,
  FireIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import imageBg from "@/assets/segments/acougue.webp";
import visaoGeralImg from "@/assets/visaogeralacougue.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));
const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// ---- Navegação fixa (mesma mecânica das demais páginas) ----
const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Desafios", href: "#desafios" },
  { label: "Categorias", href: "#categorias" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const openConsultorModal = () => {
  window.dispatchEvent(new CustomEvent("open-consultor-modal"));
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, { passive: true });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

// Desafios reais de quem toca um açougue, e como o Kali Gestão resolve cada um
// (funcionalidades confirmadas no ebook do produto).
const desafios = [
  {
    icon: ScaleIcon,
    titulo: "Venda por peso na balança",
    problema:
      "Cada corte pesa diferente e o preço por quilo muda toda hora — depender de calculadora ou anotação manual custa tempo e gera erro no caixa.",
    solucao:
      "PDV integrado à balança calcula o preço certo na hora, com baixa automática no estoque.",
  },
  {
    icon: ClockIcon,
    titulo: "Validade curta e controle de lote",
    problema:
      "Carne estraga rápido — sem controle de lote e validade, a sobra vira perda ou, pior, vai pro cliente errado.",
    solucao:
      "Lote e rastreabilidade vinculam cada venda ao lote certo, com a validade sempre visível.",
  },
  {
    icon: ExclamationTriangleIcon,
    titulo: "Perda no corte e no resfriamento",
    problema:
      "Quebra no processamento e perda de peso no resfriamento corroem a margem sem que ninguém perceba no dia a dia.",
    solucao:
      "Curva ABC e relatórios de estoque mostram exatamente onde a perda está acontecendo.",
  },
  {
    icon: TagIcon,
    titulo: "Etiqueta de preço na hora da venda",
    problema:
      "Escrever preço à mão ou errar a etiqueta na correria do movimento passa insegurança pro cliente no balcão.",
    solucao:
      "Etiquetas com código de barras e preço saem prontas, direto do cadastro do produto.",
  },
  {
    icon: BanknotesIcon,
    titulo: "Fiado pro cliente fiel",
    problema:
      "Cliente antigo compra fiado e paga no fim do mês — sem controle, fica difícil saber quem deve o quê.",
    solucao:
      "Contas a receber e financeiro mostram exatamente quem deve, quanto e quando cai no caixa.",
  },
  {
    icon: ClipboardDocumentListIcon,
    titulo: "Cotação com fornecedor de carne",
    problema:
      "O preço da arroba muda toda semana — comprar do fornecedor errado, na hora errada, come a margem do mês.",
    solucao:
      "Envie a cotação pra vários fornecedores de uma vez e compare o retorno antes de fechar a compra.",
  },
];

// Categorias de produto que um açougue precisa organizar no mesmo estoque.
const categorias = [
  { icon: CubeIcon, label: "Bovina e suína" },
  { icon: Square3Stack3DIcon, label: "Aves" },
  { icon: TagIconSolid, label: "Embutidos e frios" },
  { icon: ArchiveBoxIcon, label: "Congelados" },
  { icon: SparklesIcon, label: "Temperados e marinados" },
  { icon: FireIcon, label: "Churrasco e espetos" },
];

const funcionalidades = [
  {
    icon: CreditCardIcon,
    eyebrow: "Frente de caixa",
    titulo: "PDV com balança, TEF e PIX",
    texto: "Venda no balcão integrada à balança, com TEF, PinPad e PIX dinâmico.",
  },
  {
    icon: DocumentCheckIcon,
    eyebrow: "Rastreabilidade",
    titulo: "Lote e validade sempre em dia",
    texto: "Cada produto vinculado ao lote certo, com controle de validade automático.",
  },
  {
    icon: TagIcon,
    eyebrow: "Etiquetas",
    titulo: "Preço certo, sem erro no balcão",
    texto: "Etiquetas com código de barras direto do cadastro, sem escrever à mão.",
  },
  {
    icon: BanknotesIcon,
    eyebrow: "Financeiro",
    titulo: "Fiado e boletos organizados",
    texto: "Contas a receber, boletos e conciliação bancária num só painel.",
  },
];

// Capacidades reais do sistema — sem número inventado, só o que o Kali de fato faz.
const capacidades = [
  {
    icon: ScaleIcon,
    titulo: "Balança integrada",
    texto: "O PDV lê o peso e calcula o preço na hora, sem conta manual.",
  },
  {
    icon: ClockIcon,
    titulo: "Rastreabilidade total",
    texto: "Lote e validade acompanham cada corte, do estoque até a venda.",
  },
  {
    icon: DocumentCheckIcon,
    titulo: "Fiscal em dia",
    texto: "Sintegra, Sped Fiscal e Bloco K sempre atualizados com seu contador.",
  },
];

const documentosFiscais = ["NF-e", "NFC-e", "NFS-e", "CT-e", "CT-e OS", "MDF-e"];

const controleTributario = [
  { label: "Sped Fiscal", shade: "bg-kali-blue-700" },
  { label: "Bloco H", shade: "bg-kali-blue-600" },
  { label: "Bloco K", shade: "bg-kali-blue-500" },
  { label: "EFD Contribuições", shade: "bg-kali-blue-400" },
];

useSeo({
  title: "Sistema para Açougue e Casa de Carnes | Kali Gestão",
  description:
    "Venda por peso, controle de lote e validade, fiado pro cliente fiel e cotação de fornecedor — o Kali Gestão organiza a rotina do seu açougue.",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Gestão para Açougues",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/segmento/acougues",
    description:
      "Sistema de gestão para açougues e casas de carne: venda por peso, controle de lote e validade, fiado e cotação de fornecedor.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SegmentHero
      title="Controle total do açougue, do corte à balança"
      description="Venda por peso, validade de carne, perda no resfriamento e fiado pro cliente fiel — o Kali Gestão organiza tudo isso em um só sistema."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=acougues"
      secondary-label="Ver outros segmentos"
      secondary-href="/segmentos"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    />

    <!-- Navegação fixa -->
    <section class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <nav
          class="flex overflow-x-auto relative gap-6 items-center h-16"
          aria-label="Navegação da página"
        >
          <a
            v-for="item in navLinks"
            :key="item.href"
            :href="item.href"
            class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
            :class="{ 'text-[#006086] active': activeNav === item.href }"
            @click="(e) => handleNavClick(e, item.href)"
          >
            {{ item.label }}
          </a>
        </nav>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Açougue usando o Kali Gestão"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito pra rotina de quem toca um açougue
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Corte, peso, validade — no açougue, cada detalhe conta. O Kali
            Gestão une PDV com balança, controle de lote e validade, etiqueta
            automática e financeiro num só sistema, 100% online, pra você
            vender rápido sem perder o controle do que sai e do que sobra.
          </p>
          <div class="grid gap-4 mt-10 sm:grid-cols-3 lg:max-w-none">
            <div
              v-for="cap in capacidades"
              :key="cap.titulo"
              v-reveal
              class="p-4 bg-[#e6f0f4] rounded-2xl"
            >
              <component :is="cap.icon" class="w-6 h-6 text-kali-blue-600" />
              <p class="mt-2 text-sm font-bold text-gray-900">{{ cap.titulo }}</p>
              <p class="mt-1 text-xs leading-relaxed text-gray-600">{{ cap.texto }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Desafios reais do segmento -->
    <section
      id="desafios"
      class="py-16 bg-white scroll-mt-24"
      style="background-image: radial-gradient(circle at 15% 20%, rgba(0,96,134,0.10), transparent 45%), radial-gradient(circle at 85% 85%, rgba(229,190,53,0.12), transparent 45%);"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <span
            class="inline-flex rounded-full bg-[#e6f0f4] px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-kali-blue-700"
            >Rotina real do açougue</span
          >
          <h2
            class="mt-4 text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Seis dores que todo dono de açougue conhece
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Não é genérico: é venda por peso, é validade curta, é fiado pro
            cliente fiel. Veja como o Kali Gestão resolve cada uma.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <article
            v-for="d in desafios"
            :key="d.titulo"
            v-reveal
            class="p-7 bg-white rounded-3xl border border-white transition shadow-[0_20px_45px_-15px_rgba(0,50,58,0.35)] hover:-translate-y-1 hover:shadow-[0_28px_55px_-15px_rgba(0,50,58,0.42)]"
          >
            <component :is="d.icon" class="w-10 h-10 text-kali-blue-600" />
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ d.titulo }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ d.problema }}
            </p>
            <div class="p-4 mt-4 rounded-xl bg-[#e6f0f4]">
              <p class="text-sm leading-relaxed text-kali-blue-900">
                <span class="font-bold">Como o Kali resolve:</span>
                {{ d.solucao }}
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Categorias de produto -->
    <section id="categorias" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-3 gap-x-6 gap-y-10 sm:grid-cols-4">
          <div v-for="c in categorias" :key="c.label" v-reveal class="flex flex-col items-center text-center group">
            <component
              :is="c.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ c.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Cada corte, cada categoria, no mesmo
            <span class="text-kali-blue-600">controle</span>
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Bovina, suína, aves, embutidos — cada categoria tem seu giro e sua
            validade. O Kali organiza tudo no mesmo cadastro, com lote, curva
            ABC e etiquetas pra você nunca perder o controle do que entra e
            sai.
          </p>
          <button
            type="button"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
            @click="openConsultorModal"
          >
            Fale com um consultor
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades em destaque -->
    <section id="funcionalidades" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Do balcão ao contador, tudo conectado
          </h2>
        </div>
        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="f in funcionalidades" :key="f.titulo" v-reveal class="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <component :is="f.icon" class="w-9 h-9 text-kali-blue-600" />
            <p class="mt-4 text-xs font-bold tracking-wide text-kali-blue-600 uppercase">{{ f.eyebrow }}</p>
            <h3 class="mt-1 text-base font-bold text-gray-900">{{ f.titulo }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ f.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div v-reveal class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl">
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Gestão acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua empresa em conformidade sem parar de
              vender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold">
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg">
              <ScaleIcon class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emita documentos fiscais de forma descomplicada a cada venda no
              balcão, vinculada automaticamente ao site da Receita Federal.
            </p>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-lg font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Tributação sem complicação
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Da NFC-e do balcão ao fechamento mensal, o Painel do Contador
              entrega tudo isso integrado, sem depender de planilha paralela.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="c in controleTributario"
                :key="c.label"
                class="flex items-end p-4 h-24 font-bold text-white rounded-lg"
                :class="c.shade"
              >
                {{ c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'O sistema controla lote e validade da carne?',
            answer:
              'Sim. Cada produto pode ser vinculado a um lote com data de validade, facilitando o controle e evitando que carne vencida saia pra venda.',
          },
          {
            question: 'A etiqueta de preço sai automática por peso?',
            answer:
              'Sim. O PDV integrado à balança calcula o preço pelo peso na hora, e a etiqueta com código de barras sai direto do cadastro.',
          },
          {
            question: 'Dá pra vender fiado pro cliente fiel?',
            answer:
              'Sim. Contas a receber e financeiro organizam quem comprou fiado, quanto deve e quando vence.',
          },
          {
            question: 'O PDV integra com balança?',
            answer:
              'Sim. A frente de caixa se conecta à balança, calculando o valor certo pelo peso, sem erro manual.',
          },
        ]"
        description="Dúvidas sobre o Kali Gestão pro seu açougue? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
